import { useEffect, useState } from "react";
import { connect } from "react-redux";

import { Box, Checkbox, CircularProgress, LinearProgress, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { assessmentTemplate as omniHealthHistory } from "../../assets/assessments/omniHealthHistory";
import constants from "../../assets/constants.js";
import { runAndRecordAssessment } from "../../data/api/botpressAssessmentRunner.js";
import { getAssessmentTemplateByType } from "../../utils/helpers";
import HarnessUserSelect, { useHarnessUserSelection } from "../ui/harnessUserSelect.js";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";

// Item lifecycle while the batch runs.
const STATUS = {
  pending: "pending",
  loading: "loading",
  running: "running",
  done: "done",
  failed: "failed",
};

const statusColor = (theme, status) => {
  switch (status) {
    case STATUS.done:
      return theme.palette.success.main;
    case STATUS.failed:
      return theme.palette.error.main;
    case STATUS.loading:
    case STATUS.running:
      return theme.palette.info.main;
    default:
      return theme.palette.text.disabled;
  }
};

// The list of assessment templates supported by the current organization. Mirrors
// AssessmentSelectionDialog so the batch dialog offers exactly the same choices
// the single-assessment "Advanced Testing" picker used to.
const getSupportedAssessmentTemplates = orgODM => {
  if (orgODM != null) {
    const theReturn = [];
    for (let index = 0; index < orgODM.omniData.supportedAssessmentTypes.length; ++index) {
      const aTemplate = getAssessmentTemplateByType(orgODM.omniData.supportedAssessmentTypes[index]);
      if (aTemplate) {
        theReturn.push(aTemplate);
      }
    }
    if (theReturn.length === 0) {
      theReturn.push(getAssessmentTemplateByType(constants.assessmentIDs.botpressDefault));
    }
    return theReturn;
  }

  return [omniHealthHistory];
};

// Pull the ordered question text out of an assessment template.
const getTemplateQuestions = template =>
  (template?.omniData?.questions || [])
    .map(question => question?.text || "")
    .filter(text => text.trim().length > 0);

// Dialog that runs every selected assessment against the bot through the Chat
// API (createUser -> createConversation -> sendMessage), one after the other,
// with no webchat windows. Shows overall progress plus a per-assessment status
// list.
//
// Each assessment's scripted questions are replayed against the run-target
// (TEST) bot, and every successful run is recorded as an Assessment on that bot
// — so the run shows up in the Assessments widget for buy-off, exactly like the
// interactive "Advanced Testing" flow, but for the whole suite at once.
const BotpressAssessmentBatchRunDialog = props => {
  const {
    open,
    onClose,
    // Run target: the assessments are replayed against this bot and their
    // assessment records are written here (the TEST bot).
    botODM,
    userODM,
    configurations,
    // Configuration rows for the run-target (TEST) bot, used to stamp the
    // assessment version.
    testConfigurations,
    initialUserIndex = null,
    // The org whose supported assessment templates are offered. Supplied by
    // redux (mapStateToProps) but can be overridden via props.
    organizationODM,
    // Called once after the batch finishes if any assessments were created, so
    // the caller can refresh the Assessments widget.
    onAssessmentsCreated,
    dialogTitle = "Run All Assessments",
    maxWidth = "sm",
    fullWidth = true,
  } = props;

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const { harnessUsers, selectedUserIndex, setSelectedUserIndex, selectedUserData } =
    useHarnessUserSelection(botODM, initialUserIndex);

  const [isRunning, setIsRunning] = useState(false);
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [statusText, setStatusText] = useState("");

  // Build the selectable list of assessments when opened.
  useEffect(() => {
    if (!open) {
      return;
    }

    const templates = getSupportedAssessmentTemplates(organizationODM);
    const list = templates
      .map((template, index) => {
        const questions = getTemplateQuestions(template);
        return {
          key: `${template?.omniData?.omniType || "assessment"}-${index}`,
          template,
          title: template?.omniData?.title || "Assessment",
          questionCount: questions.length,
          selected: true,
          status: STATUS.pending,
          newConversationId: null,
          detail: `${questions.length} question${questions.length === 1 ? "" : "s"}`,
        };
      })
      .filter(item => item.questionCount > 0);

    setItems(list);
    setStatusText(
      list.length === 0
        ? "No assessments available to run."
        : `${list.length} assessment${list.length === 1 ? "" : "s"} ready to run.`,
    );
    // Disable eslint because the org list is stable while the dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateItem = (index, patch) => {
    setItems(prev => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const toggleItem = index => {
    setItems(prev =>
      prev.map((item, i) => (i === index ? { ...item, selected: !item.selected } : item)),
    );
  };

  const toggleAll = checked => {
    setItems(prev => prev.map(item => ({ ...item, selected: checked })));
  };

  const selectedCount = items.filter(item => item.selected).length;

  const handleRun = async () => {
    if (selectedCount === 0) {
      return;
    }

    setIsRunning(true);
    const total = selectedCount;
    let started = 0;
    let failed = 0;
    let assessmentsCreated = 0;
    const failures = [];

    // Sequential on purpose: each run creates its own user + conversation, and
    // running them one at a time keeps the load on Botpress predictable.
    for (let i = 0; i < items.length; i++) {
      // Skip assessments the user unchecked.
      if (!items[i].selected) {
        continue;
      }
      started++;
      setCurrentIndex(i);
      const source = items[i];
      const questions = getTemplateQuestions(source.template);
      updateItem(i, { status: STATUS.loading, detail: "Starting…" });
      setStatusText(`Running assessment ${started} of ${total}…`);

      try {
        const result = await runAndRecordAssessment({
          botODM,
          questions,
          userData: selectedUserData,
          userODM,
          configurations,
          testConfigurations,
          assessmentType: source.template?.omniData?.omniType || "Custom",
          assessmentName: source.title,
          onProgress: event => {
            if (event.phase === "sending") {
              updateItem(i, {
                status: STATUS.running,
                detail: `Question ${event.index + 1} of ${event.total}…`,
              });
            }
          },
        });

        if (result.assessmentCreated) {
          assessmentsCreated++;
          updateItem(i, {
            status: STATUS.done,
            newConversationId: result.conversationId,
            detail: `${questions.length} questions replayed · assessment created`,
          });
        } else {
          failed++;
          failures.push(
            `${source.title}: assessment not saved (${result.assessmentError.message})`,
          );
          updateItem(i, {
            status: STATUS.failed,
            newConversationId: result.conversationId,
            detail: `ran, but assessment not saved: ${result.assessmentError.message}`,
          });
        }
      } catch (error) {
        failed++;
        failures.push(`${source.title}: ${error.message}`);
        console.error(`Assessment run failed for template ${source.title}:`, error);
        updateItem(i, {
          status: STATUS.failed,
          detail: error.message || "Run failed",
        });
      }
    }

    setCurrentIndex(-1);
    setIsRunning(false);
    // Keep the outcome (including any failures and why) visible after the run so
    // it doesn't vanish when the dialog is closed. Per-row detail also persists.
    const summary = `Done. ${assessmentsCreated} of ${total} assessment${assessmentsCreated === 1 ? "" : "s"} created${failed ? `, ${failed} failed` : ""}.`;
    setStatusText(failures.length > 0 ? `${summary} ${failures.join("; ")}` : summary);
    enqueueSnackbar(summary, { variant: failed ? "warning" : "success" });

    if (assessmentsCreated > 0 && typeof onAssessmentsCreated === "function") {
      onAssessmentsCreated();
    }
  };

  const onCloseHandler = () => {
    if (isRunning) {
      return;
    }
    setItems([]);
    setCurrentIndex(-1);
    setStatusText("");
    onClose();
  };

  const finishedCount = items.filter(
    item => item.selected && (item.status === STATUS.done || item.status === STATUS.failed),
  ).length;
  const overallProgress =
    selectedCount > 0 ? Math.round((finishedCount / selectedCount) * 100) : 0;

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <DialogMessage>
          Choose which assessments to run. Selected ones are run against the test bot through the
          Chat API — no webchat windows are opened — and each successful run is saved as an
          Assessment. Progress is shown below.
        </DialogMessage>

        <HarnessUserSelect
          harnessUsers={harnessUsers}
          value={selectedUserIndex}
          onChange={setSelectedUserIndex}
          disabled={isRunning}
          id="assessmentBatchRunSelectUser"
          sx={{ marginTop: theme.spacing(1) }}
        />

        {(isRunning || statusText) && (
          <Box sx={{ marginTop: theme.spacing(1) }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
              {isRunning && <CircularProgress size={18} color="secondary" />}
              <Typography variant="body2" color="textSecondary">
                {statusText}
              </Typography>
            </Box>
            {isRunning && (
              <LinearProgress
                color="secondary"
                variant="determinate"
                value={overallProgress}
                sx={{ marginTop: theme.spacing(1), borderRadius: theme.spacing(0.5) }}
              />
            )}
          </Box>
        )}

        {items.length > 0 && (
          <Box
            sx={{
              marginTop: theme.spacing(2),
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.spacing(1),
              maxHeight: "40vh",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                padding: theme.spacing(0.5),
                paddingLeft: theme.spacing(1),
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.commonground.main,
                zIndex: 1,
              }}
            >
              <Checkbox
                size="small"
                color="secondary"
                disabled={isRunning}
                checked={items.length > 0 && selectedCount === items.length}
                indeterminate={selectedCount > 0 && selectedCount < items.length}
                onChange={event => toggleAll(event.target.checked)}
              />
              <Typography variant="caption" color="textSecondary">
                {selectedCount} of {items.length} selected
              </Typography>
            </Box>
            {items.map((item, index) => (
              <Box
                key={item.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: theme.spacing(1),
                  borderBottom:
                    index < items.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                  backgroundColor:
                    index === currentIndex ? theme.palette.action.hover : "transparent",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
                  <Checkbox
                    size="small"
                    color="secondary"
                    disabled={isRunning}
                    checked={item.selected}
                    onChange={() => toggleItem(index)}
                  />
                  <Box>
                    <Typography variant="body2">{item.title}</Typography>
                    {item.detail && (
                      <Typography variant="caption" color="textSecondary">
                        {item.detail}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    color: statusColor(theme, item.status),
                  }}
                >
                  {item.status}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseHandler} color="secondary" variant="contained" disabled={isRunning}>
          Close
        </Button>
        <Button
          onClick={handleRun}
          color="secondary"
          variant="contained"
          disabled={isRunning || selectedCount === 0}
        >
          {isRunning ? "Running…" : `Run Selected (${selectedCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = state => ({
  organizationODM: state.userSession.organizationODM,
});

export default connect(mapStateToProps)(BotpressAssessmentBatchRunDialog);
